import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BackIcon } from '../components/icons'
import { useLanguage } from '../context/LanguageContext'
import { useClient } from '../context/ClientContext'
import { useTenant } from '../context/TenantContext'
import InquiryService from '../services/inquiry.service'
import Loader from '../components/ui/loader'
import NotFound from './NotFound'
import { isCustomerInquiryEnabled } from '../utils/tenantFeatures'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'

const InquiryCategoryForms = () => {
  const { categoryId, tenanturlname } = useParams()
  const navigate = useNavigate()
  const { t, languageCode } = useLanguage()
  const { isAuthenticated } = useClient()
  const { tenant } = useTenant()
  const [selectedFormId, setSelectedFormId] = useState('')
  const [category, setCategory] = useState(null)
  const [filteredForms, setFilteredForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [categoryNotFound, setCategoryNotFound] = useState(false)

  useEffect(() => {
    const fetchCategory = async () => {
      if (!isCustomerInquiryEnabled(tenant)) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setCategoryNotFound(false)
        const schemas = await InquiryService.getInquiryForms(
          tenant.tenantid,
          languageCode,
        )

        if (schemas && schemas.inquiryCategories) {
          // Find the category by ID
          const foundCategory = schemas.inquiryCategories.find(
            (cat) => cat.id === categoryId,
          )

          if (foundCategory) {
            // Filter forms based on authentication status
            const availableForms = foundCategory.forms.filter((form) => {
              // If form requires auth and user is not authenticated, hide it
              if (form.requireAuth && !isAuthenticated) {
                return false
              } else if (form.publicOnly && isAuthenticated) {
                return false
              }
              return true
            })

            setCategory(foundCategory)
            setFilteredForms(availableForms)
          } else {
            // Removed/unknown category ids show the branded 404 page
            setCategory(null)
            setCategoryNotFound(true)
          }
        }
      } catch (error) {
        console.error('Error fetching inquiry category:', error)
      } finally {
        setLoading(false)
      }
    }

    if (tenant?.tenantid) {
      fetchCategory()
    }
  }, [tenant, categoryId, isAuthenticated, languageCode])

  if (loading) {
    return (
      <Loader 
        fullscreen 
        label={t('common.loading')} 
        size="xl"
      />
    )
  }

  if ((tenant && !isCustomerInquiryEnabled(tenant)) || categoryNotFound) {
    return <NotFound />
  }

  if (!category) {
    return (
      <div className="inquiry-form-page">
        <div className="flex flex-col items-center mx-auto mt-8 px-4">
          <div className="w-full flex items-center mb-6">
            <button
              onClick={() => navigate(`/${tenanturlname}/inquiry`)}
              className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
            >
              <BackIcon className="h-5 w-5 text-theme-primary" />
            </button>
            <h1 className="text-base font-extrabold text-theme-secondary">
              {t('inquiry.categoryNotFound')}
            </h1>
          </div>
        </div>
      </div>
    )
  }

  const handleFormSelect = (formId) => {
    setSelectedFormId(formId)
    // Navigate to the form page
    navigate(`/${tenanturlname}/inquiry/${formId}`)
  }

  return (
    <div className="inquiry-form-page">
      <div className="flex flex-col items-center mx-auto mt-8 px-4">
        {/* Header with back button */}
        <div className="w-full flex items-center mb-6">
          <button
            onClick={() => navigate(`/${tenanturlname}/inquiry`)}
            className="cursor-pointer text-blue-900 mr-1 hover:bg-theme-primary/10 rounded-full p-2"
          >
            <BackIcon className="h-5 w-5 text-theme-primary" />
          </button>
          <h1 className="text-base font-extrabold text-theme-secondary">
            {t(category.label)}
          </h1>
        </div>

        {/* Inquiry Type Selection */}
        <div className="w-full md:w-1/2 md:self-start">
          <label
            htmlFor="inquiry-type"
            className="block text-sm font-medium mb-2 text-left"
          >
            {t('inquiry.inquiryType')}
          </label>
          {filteredForms.length === 0 ? (
            <div className="py-8 px-5 text-center text-gray-500 bg-white rounded-md">
              <p>{t('inquiry.noInquiryFormsAvailable')}</p>
            </div>
          ) : (
            <Select value={selectedFormId} onValueChange={handleFormSelect}>
              <SelectTrigger className="w-full rounded-md">
                <SelectValue placeholder={t('inquiry.selectInquiryType')} />
              </SelectTrigger>
              <SelectContent>
                {filteredForms.map((form) => (
                  <SelectItem key={form.id} value={form.id}>
                    {t(form.title)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
    </div>
  )
}

export default InquiryCategoryForms
